import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/config';
import DashboardHeader from '../common/DashboardHeader';
import {
  WrenchScrewdriverIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  ClockIcon,
  StarIcon,
  TrophyIcon,
  ChartBarIcon,
  CalendarIcon,
  UserGroupIcon,
  BellIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  FireIcon,
  SparklesIcon,
  LightBulbIcon,
  FlagIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  CameraIcon,
  PaperClipIcon,
  ChevronRightIcon,
  TagIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { Line, Doughnut, Bar, Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface Task {
  id: string;
  jobCardId: string;
  title: string;
  description: string;
  vehicle: string;
  customer: string;
  priority: 'urgent' | 'high' | 'medium' | 'low';
  status: 'pending' | 'in_progress' | 'paused' | 'completed';
  estimatedTime: number; // in minutes
  actualTime?: number;
  startTime?: string;
  endTime?: string;
  assignedAt: string;
  dueTime: string;
  checklist?: {
    item: string;
    completed: boolean;
  }[];
  notes?: string;
  images?: string[];
}

interface EmployeeStats {
  todayTasks: number;
  completedTasks: number;
  inProgressTasks: number;
  avgCompletionTime: number;
  performanceScore: number;
  customerRating: number;
  monthlyTasks: number;
  efficiencyRate: number;
  onTimeRate: number;
  totalRevenue: number;
  bonusEarned: number;
}

interface PerformanceMetrics {
  weekly: {
    tasks: number[];
    efficiency: number[];
    revenue: number[];
  };
  monthly: {
    categories: { name: string; count: number }[];
    ratings: number[];
  };
  skills: {
    label: string;
    score: number;
  }[];
}

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  progress: number;
  maxProgress: number;
  unlockedAt?: string;
  reward?: string;
}

const EnhancedEmployeeDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<EmployeeStats | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [currentTask, setCurrentTask] = useState<Task | null>(null);
  const [performance, setPerformance] = useState<PerformanceMetrics | null>(null);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState('overview');
  const [taskFilter, setTaskFilter] = useState('all');
  const [timer, setTimer] = useState<number>(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isTimerRunning && currentTask) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, currentTask]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // In production, these would be actual API calls
      // For now, setting mock data
      setMockData();
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const setMockData = () => {
    setStats({
      todayTasks: 12,
      completedTasks: 8,
      inProgressTasks: 1,
      avgCompletionTime: 45,
      performanceScore: 92,
      customerRating: 4.8,
      monthlyTasks: 186,
      efficiencyRate: 94.5,
      onTimeRate: 97.2,
      totalRevenue: 4250.750,
      bonusEarned: 425.075
    });

    setTasks([
      {
        id: '1',
        jobCardId: 'JC-2024-001',
        title: 'Oil Change & Filter Replacement',
        description: 'Regular oil change service with filter replacement',
        vehicle: 'Toyota Camry 2020',
        customer: 'Ahmed Al-Rashid',
        priority: 'medium',
        status: 'in_progress',
        estimatedTime: 30,
        actualTime: 25,
        startTime: '09:00',
        assignedAt: '08:45',
        dueTime: '09:30',
        checklist: [
          { item: 'Drain old oil', completed: true },
          { item: 'Replace oil filter', completed: true },
          { item: 'Add new oil', completed: false },
          { item: 'Check oil level', completed: false },
          { item: 'Reset service indicator', completed: false }
        ],
        notes: 'Customer requested 5W-30 synthetic oil'
      },
      {
        id: '2',
        jobCardId: 'JC-2024-002',
        title: 'Brake Pad Replacement',
        description: 'Front brake pads replacement',
        vehicle: 'Honda Accord 2019',
        customer: 'Fatima Hassan',
        priority: 'high',
        status: 'pending',
        estimatedTime: 90,
        assignedAt: '09:30',
        dueTime: '11:00',
        checklist: [
          { item: 'Remove wheels', completed: false },
          { item: 'Remove old brake pads', completed: false },
          { item: 'Install new brake pads', completed: false },
          { item: 'Bleed brake system', completed: false },
          { item: 'Test brakes', completed: false }
        ]
      },
      {
        id: '3',
        jobCardId: 'JC-2024-003',
        title: 'AC System Check',
        description: 'AC performance check and refrigerant recharge',
        vehicle: 'Nissan Altima 2021',
        customer: 'Mohammed Ali',
        priority: 'urgent',
        status: 'pending',
        estimatedTime: 60,
        assignedAt: '10:00',
        dueTime: '11:00',
        notes: 'Customer complained about weak cooling'
      },
      {
        id: '4',
        jobCardId: 'JC-2024-004',
        title: 'Engine Diagnostics',
        description: 'Check engine light diagnosis',
        vehicle: 'BMW 320i 2018',
        customer: 'Sara Ibrahim',
        priority: 'medium',
        status: 'completed',
        estimatedTime: 45,
        actualTime: 40,
        startTime: '08:00',
        endTime: '08:40',
        assignedAt: '07:45',
        dueTime: '08:45'
      }
    ]);

    setCurrentTask(tasks[0]); // Set the in-progress task as current

    setPerformance({
      weekly: {
        tasks: [32, 35, 38, 36, 40, 42, 38],
        efficiency: [92, 94, 93, 95, 96, 94, 95],
        revenue: [850, 920, 880, 950, 1020, 980, 920]
      },
      monthly: {
        categories: [
          { name: 'Oil Change', count: 45 },
          { name: 'Brake Service', count: 32 },
          { name: 'AC Service', count: 28 },
          { name: 'Engine Repair', count: 22 },
          { name: 'Diagnostics', count: 18 },
          { name: 'Other', count: 41 }
        ],
        ratings: [4.7, 4.8, 4.6, 4.9, 4.8, 4.7]
      },
      skills: [
        { label: 'Technical Skills', score: 95 },
        { label: 'Speed', score: 92 },
        { label: 'Quality', score: 98 },
        { label: 'Customer Service', score: 90 },
        { label: 'Problem Solving', score: 88 },
        { label: 'Team Work', score: 93 }
      ]
    });

    setAchievements([
      {
        id: '1',
        title: 'Speed Demon',
        description: 'Complete 50 tasks under estimated time',
        icon: <FireIcon className="w-8 h-8 text-orange-600" />,
        progress: 47,
        maxProgress: 50,
        reward: '50 KWD Bonus'
      },
      {
        id: '2',
        title: 'Customer Champion',
        description: 'Maintain 4.8+ rating for a month',
        icon: <StarIcon className="w-8 h-8 text-yellow-600" />,
        progress: 28,
        maxProgress: 30,
        unlockedAt: '2024-01-15',
        reward: 'Customer Service Badge'
      },
      {
        id: '3',
        title: 'Quality Master',
        description: '100 tasks with zero rework',
        icon: <TrophyIcon className="w-8 h-8 text-purple-600" />,
        progress: 92,
        maxProgress: 100
      },
      {
        id: '4',
        title: 'Revenue Generator',
        description: 'Generate 5000 KWD in a month',
        icon: <SparklesIcon className="w-8 h-8 text-green-600" />,
        progress: 4250,
        maxProgress: 5000,
        reward: '10% Commission Bonus'
      }
    ]);
  };

  const startTask = (task: Task) => {
    setCurrentTask(task);
    setIsTimerRunning(true);
    setTimer(0);
    // Update task status
    setTasks(prev => prev.map(t => 
      t.id === task.id ? { ...t, status: 'in_progress', startTime: new Date().toLocaleTimeString() } : t
    ));
  };

  const pauseTask = () => {
    setIsTimerRunning(false);
  };

  const resumeTask = () => {
    setIsTimerRunning(true);
  };

  const completeTask = () => {
    if (currentTask) {
      const actualTime = Math.floor(timer / 60);
      setTasks(prev => prev.map(t => 
        t.id === currentTask.id 
          ? { ...t, status: 'completed', actualTime, endTime: new Date().toLocaleTimeString() } 
          : t
      ));
      setCurrentTask(null);
      setIsTimerRunning(false);
      setTimer(0);
      
      // Update stats
      setStats(prev => prev ? {
        ...prev,
        completedTasks: prev.completedTasks + 1,
        inProgressTasks: Math.max(0, prev.inProgressTasks - 1)
      } : null);
    }
  };

  const updateChecklistItem = (taskId: string, itemIndex: number) => {
    setTasks(prev => prev.map(task => {
      if (task.id === taskId && task.checklist) {
        const updatedChecklist = [...task.checklist];
        updatedChecklist[itemIndex].completed = !updatedChecklist[itemIndex].completed;
        return { ...task, checklist: updatedChecklist };
      }
      return task;
    }));
  };

  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KW', {
      style: 'currency',
      currency: 'KWD',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(amount);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const renderCurrentTaskCard = () => {
    if (!currentTask) {
      return (
        <div className="bg-gray-100 rounded-xl p-8 text-center">
          <ClockIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No active task. Select a task to start working.</p>
        </div>
      );
    }

    const progress = currentTask.checklist 
      ? (currentTask.checklist.filter(item => item.completed).length / currentTask.checklist.length) * 100
      : 0;

    return (
      <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-[#008B8B]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">{currentTask.title}</h3>
            <p className="text-sm text-gray-600">{currentTask.vehicle} - {currentTask.customer}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(currentTask.priority)}`}>
            {currentTask.priority.toUpperCase()}
          </span>
        </div>

        {/* Timer */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Time Elapsed</p>
              <p className="text-3xl font-bold text-gray-900">{formatTime(timer)}</p>
              <p className="text-xs text-gray-500">Estimated: {currentTask.estimatedTime} mins</p>
            </div>
            <div className="flex space-x-2">
              {isTimerRunning ? (
                <>
                  <button
                    onClick={pauseTask}
                    className="p-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
                  >
                    <PauseIcon className="w-6 h-6" />
                  </button>
                  <button
                    onClick={completeTask}
                    className="p-3 bg-green-500 text-white rounded-lg hover:bg-green-600"
                  >
                    <CheckCircleIcon className="w-6 h-6" />
                  </button>
                </>
              ) : (
                <button
                  onClick={resumeTask}
                  className="p-3 bg-[#008B8B] text-white rounded-lg hover:bg-[#006666]"
                >
                  <PlayIcon className="w-6 h-6" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Task Progress</span>
            <span className="text-sm font-medium text-gray-900">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-[#008B8B] h-2 rounded-full transition-all duration-300"
              style={{width: `${progress}%`}}
            ></div>
          </div>
        </div>

        {/* Checklist */}
        {currentTask.checklist && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Checklist</h4>
            {currentTask.checklist.map((item, index) => (
              <label key={index} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                <input
                  type="checkbox"
                  checked={item.completed}
                  onChange={() => updateChecklistItem(currentTask.id, index)}
                  className="w-4 h-4 text-[#008B8B] border-gray-300 rounded focus:ring-[#008B8B]"
                />
                <span className={`text-sm ${item.completed ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                  {item.item}
                </span>
              </label>
            ))}
          </div>
        )}

        {/* Notes */}
        {currentTask.notes && (
          <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
            <p className="text-sm text-yellow-800">
              <InformationCircleIcon className="w-4 h-4 inline mr-1" />
              {currentTask.notes}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 flex space-x-3">
          <button className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center">
            <ChatBubbleLeftRightIcon className="w-5 h-5 mr-2" />
            Add Note
          </button>
          <button className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center">
            <CameraIcon className="w-5 h-5 mr-2" />
            Add Photo
          </button>
        </div>
      </div>
    );
  };

  const renderTaskList = () => {
    const filteredTasks = tasks.filter(task => {
      if (taskFilter === 'all') return true;
      return task.status === taskFilter;
    });

    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Today's Tasks</h3>
          <select
            value={taskFilter}
            onChange={(e) => setTaskFilter(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All Tasks</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        <div className="space-y-3 max-h-96 overflow-y-auto">
          {filteredTasks.map((task) => (
            <div 
              key={task.id}
              className={`border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${
                task.status === 'completed' ? 'bg-gray-50 opacity-75' : 'bg-white'
              } ${currentTask?.id === task.id ? 'ring-2 ring-[#008B8B]' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center">
                    <h4 className="font-medium text-gray-900">{task.title}</h4>
                    <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${getPriorityColor(task.priority)}`}>
                      {task.priority}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {task.vehicle} - {task.customer}
                  </p>
                  <div className="flex items-center mt-2 text-xs text-gray-500">
                    <ClockIcon className="w-4 h-4 mr-1" />
                    <span>Est: {task.estimatedTime} mins</span>
                    {task.actualTime && (
                      <span className="ml-3 text-green-600">
                        Actual: {task.actualTime} mins
                      </span>
                    )}
                  </div>
                </div>
                <div className="ml-4">
                  {task.status === 'pending' && (
                    <button
                      onClick={() => startTask(task)}
                      className="px-3 py-1 bg-[#008B8B] text-white text-sm rounded-lg hover:bg-[#006666]"
                    >
                      Start
                    </button>
                  )}
                  {task.status === 'in_progress' && (
                    <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-lg">
                      <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse mr-2"></span>
                      In Progress
                    </span>
                  )}
                  {task.status === 'completed' && (
                    <CheckCircleIcon className="w-6 h-6 text-green-600" />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPerformanceCharts = () => {
    if (!performance) return null;

    const weeklyData = {
      labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      datasets: [
        {
          label: 'Tasks Completed',
          data: performance.weekly.tasks,
          borderColor: 'rgb(0, 139, 139)',
          backgroundColor: 'rgba(0, 139, 139, 0.1)',
          yAxisID: 'y',
        },
        {
          label: 'Efficiency %',
          data: performance.weekly.efficiency,
          borderColor: 'rgb(255, 140, 0)',
          backgroundColor: 'rgba(255, 140, 0, 0.1)',
          yAxisID: 'y1',
        }
      ]
    };

    const weeklyOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      scales: {
        y: {
          type: 'linear' as const,
          display: true,
          position: 'left' as const,
          title: {
            display: true,
            text: 'Tasks'
          }
        },
        y1: {
          type: 'linear' as const,
          display: true,
          position: 'right' as const,
          title: {
            display: true,
            text: 'Efficiency %'
          },
          grid: {
            drawOnChartArea: false,
          },
          min: 80,
          max: 100
        },
      },
    };

    const skillsData = {
      labels: performance.skills.map(s => s.label),
      datasets: [{
        label: 'Skill Level',
        data: performance.skills.map(s => s.score),
        borderColor: 'rgb(0, 139, 139)',
        backgroundColor: 'rgba(0, 139, 139, 0.2)',
        pointBackgroundColor: 'rgb(0, 139, 139)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgb(0, 139, 139)'
      }]
    };

    const skillsOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        r: {
          beginAtZero: true,
          max: 100,
          ticks: {
            stepSize: 20
          }
        }
      },
      plugins: {
        legend: {
          display: false
        }
      }
    };

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Weekly Performance</h3>
          <div className="h-64">
            <Line data={weeklyData} options={weeklyOptions as any} />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Skills Assessment</h3>
          <div className="h-64">
            <Radar data={skillsData} options={skillsOptions} />
          </div>
        </div>
      </div>
    );
  };

  const renderAchievements = () => {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Achievements & Goals</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {achievements.map((achievement) => (
            <div
              key={achievement.id}
              className={`border rounded-lg p-4 ${
                achievement.unlockedAt ? 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-300' : 'bg-white border-gray-200'
              }`}
            >
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {achievement.icon}
                </div>
                <div className="ml-4 flex-1">
                  <h4 className="font-medium text-gray-900">{achievement.title}</h4>
                  <p className="text-sm text-gray-600 mt-1">{achievement.description}</p>
                  
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-600">Progress</span>
                      <span className="font-medium">
                        {achievement.progress}/{achievement.maxProgress}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          achievement.unlockedAt ? 'bg-gradient-to-r from-yellow-400 to-orange-400' : 'bg-[#008B8B]'
                        }`}
                        style={{width: `${(achievement.progress / achievement.maxProgress) * 100}%`}}
                      ></div>
                    </div>
                  </div>

                  {achievement.reward && (
                    <p className="text-xs text-green-600 mt-2">
                      🎁 Reward: {achievement.reward}
                    </p>
                  )}
                  {achievement.unlockedAt && (
                    <p className="text-xs text-orange-600 mt-2">
                      ✅ Unlocked on {achievement.unlockedAt}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader 
        userName={user?.name}
        userRole="employee"
        showNotifications={true}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Welcome back, {user?.name || 'Technician'}
              </h1>
              <p className="text-gray-600 mt-1">
                Track your tasks and monitor your performance
              </p>
            </div>
            <div className="mt-4 lg:mt-0 flex flex-wrap gap-3">
              <div className="flex bg-white rounded-lg shadow">
                {['overview', 'tasks', 'performance'].map((view) => (
                  <button
                    key={view}
                    onClick={() => setSelectedView(view)}
                    className={`px-4 py-2 text-sm font-medium capitalize ${
                      selectedView === view
                        ? 'bg-[#008B8B] text-white'
                        : 'text-gray-700 hover:bg-gray-50'
                    } ${
                      view === 'overview' ? 'rounded-l-lg' : 
                      view === 'performance' ? 'rounded-r-lg' : ''
                    }`}
                  >
                    {view}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-[#008B8B]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Today's Tasks</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">{stats?.todayTasks || 0}</p>
                <div className="mt-2 text-sm text-gray-500">
                  {stats?.completedTasks || 0} completed
                </div>
              </div>
              <ClipboardDocumentListIcon className="w-8 h-8 text-[#008B8B]" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-[#FF8C00]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Performance Score</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">{stats?.performanceScore || 0}%</p>
                <div className="mt-2 flex items-center">
                  <ArrowUpIcon className="w-4 h-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600 font-medium">+3.2%</span>
                </div>
              </div>
              <ChartBarIcon className="w-8 h-8 text-[#FF8C00]" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-[#10B981]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Customer Rating</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">⭐ {stats?.customerRating || 0}</p>
                <div className="mt-2 text-sm text-gray-500">
                  Based on 45 reviews
                </div>
              </div>
              <StarIcon className="w-8 h-8 text-[#10B981]" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-[#8B5CF6]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Bonus Earned</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">{formatCurrency(stats?.bonusEarned || 0)}</p>
                <div className="mt-2 text-sm text-gray-500">
                  This month
                </div>
              </div>
              <TrophyIcon className="w-8 h-8 text-[#8B5CF6]" />
            </div>
          </div>
        </div>

        {/* Main Content */}
        {selectedView === 'overview' && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {renderCurrentTaskCard()}
              {renderTaskList()}
            </div>
            {renderAchievements()}
          </>
        )}

        {selectedView === 'tasks' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              {renderTaskList()}
            </div>
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Task Statistics</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-600">Efficiency Rate</span>
                      <span className="text-sm font-medium">{stats?.efficiencyRate || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full"
                        style={{width: `${stats?.efficiencyRate || 0}%`}}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-600">On-Time Completion</span>
                      <span className="text-sm font-medium">{stats?.onTimeRate || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full"
                        style={{width: `${stats?.onTimeRate || 0}%`}}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-600">Monthly Progress</span>
                      <span className="text-sm font-medium">{stats?.monthlyTasks || 0}/200</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-purple-500 h-2 rounded-full"
                        style={{width: `${((stats?.monthlyTasks || 0) / 200) * 100}%`}}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Tips</h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <LightBulbIcon className="w-5 h-5 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
                    <p className="text-sm text-gray-700">
                      Complete tasks under estimated time to boost your performance score
                    </p>
                  </div>
                  <div className="flex items-start">
                    <SparklesIcon className="w-5 h-5 text-purple-500 mt-0.5 mr-2 flex-shrink-0" />
                    <p className="text-sm text-gray-700">
                      Use the checklist feature to ensure quality work
                    </p>
                  </div>
                  <div className="flex items-start">
                    <StarIcon className="w-5 h-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <p className="text-sm text-gray-700">
                      Customer feedback directly impacts your monthly bonus
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedView === 'performance' && (
          <div className="space-y-6">
            {renderPerformanceCharts()}
            {renderAchievements()}
            
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <p className="text-sm text-gray-600">Total Revenue Generated</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{formatCurrency(stats?.totalRevenue || 0)}</p>
                  <p className="text-xs text-green-600 mt-1">+12% from last month</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600">Average Task Time</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{stats?.avgCompletionTime || 0} mins</p>
                  <p className="text-xs text-blue-600 mt-1">5 mins faster than target</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600">Tasks Completed</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{stats?.monthlyTasks || 0}</p>
                  <p className="text-xs text-purple-600 mt-1">93% completion rate</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default EnhancedEmployeeDashboard;